import clsx from 'clsx';
import {useEffect, useRef, useState} from 'preact/hooks';
import React, {MouseEvent, ReactChild, ElementType} from 'react';
import defaultTheme from './added-drawer.module.pcss';

export type Props = {
  checkoutUrl: string;
  successMessage: string;
  className?: string;
  children?: ReactChild;
  testMessage: string;
  bagLinkText?: string;
  bagUrl?: string;
  productTitle: string;
  productPrice: string;
  productFixedPrice?: string;
  productImage: string;
  selectedOptions: string[];
  cartQuantity?: number;
  productComplexColor?: string;
  autoCloseDelay?: number;
  /**
   * Checkout link text
   * @default 'Continue to checkout'
   */
  checkoutLinkText?: string;
  checkoutIdClass?: string;
  /**
   * Close action text
   * @default 'Close'
   */
  closeText?: string;
  /**
   * Should display on render
   * @default false
   */
  isOpen?: boolean;
  /**
   * Element node
   * @default 'aside'
   */
  as?: ElementType;
  theme?: {
    root?: string;
    message?: string;
    close?: string;
    closeLabel?: string;
    container?: string;
    open?: string;
    checkoutButton?: string;
    bagButton?: string;
    productCard?: string;
    productDetails?: string;
    productTitle?: string;
    productPrice?: string;
    productImage?: string;
    productOption?: string;
    productComplexColor?: string;
    actionsWrapper?: string;
  };
};

export const AddedDrawer = ({
  as: ComponentProp = 'aside',
  isOpen = false,
  closeText = 'Close this modal',
  checkoutLinkText,
  checkoutIdClass,
  successMessage,
  checkoutUrl,
  className,
  children,
  theme = defaultTheme,
  testMessage,
  bagLinkText,
  bagUrl,
  productTitle,
  productFixedPrice,
  productPrice,
  productImage,
  selectedOptions = [],
  cartQuantity,
  productComplexColor,
  autoCloseDelay = 0,
  ...rest
}: Props) => {
  let autoCloseTimeout: number;
  const [open, setOpen] = useState(isOpen);
  const [drawerHovered, setDrawerHovered] = useState(false);
  const [productOptions, setProductOptions] = useState(selectedOptions);
  const [currentQuantity, setCurrentQuantity] = useState(cartQuantity);
  const [variantPrice, setVariantPrice] = useState(productPrice);
  const [variantFixedPrice, setVariantFixedPrice] = useState(productFixedPrice);
  const [localisedCheckoutUrl, setLocalisedCheckoutUrl] = useState(checkoutUrl);

  const openRef = useRef(open);
  openRef.current = open;

  const drawerHoveredRef = useRef(drawerHovered);
  drawerHoveredRef.current = drawerHovered;

  useEffect(() => {
    window.addEventListener('cart:updated', openDrawer);

    return () => {
      window.removeEventListener('cart:updated', openDrawer);
    };
  }, []);

  const closeDrawer = () => {
    window.clearTimeout(autoCloseTimeout);
    setOpen(false);
  };

  const handleCloseKeyDown = (event: React.KeyboardEvent) => {
    // Valid close keys are enter/escape/spacebar, respectively
    const closeKeys = [13, 27, 32];

    if (closeKeys.includes(event.keyCode)) {
      closeDrawer();
    }
  };

  const handleClick = (event: MouseEvent) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    setOpen(false);
  };

  const startCloseTimer = () => {
    if (autoCloseDelay === 0) {
      return;
    }

    autoCloseTimeout = window.setTimeout(autoCloseDrawer, autoCloseDelay);
  };

  const autoCloseDrawer = () => {
    // Avoid attempting to close if the drawer is already closed, or is being hovered
    if (!openRef.current || drawerHoveredRef.current) {
      return;
    }

    closeDrawer();
  };

  const openDrawer = () => {
    getCheckoutUrl();
    getProductOptions();
    getProductPrice();
    setCurrentQuantity(window.theme.cart_qty);
    setOpen(true);
    startCloseTimer();
  };

  const getCheckoutUrl = () => {
    setLocalisedCheckoutUrl("/checkout");
  }

  const getProductOptions = () => {
    const optionGroups = document.querySelectorAll('[data-options-group]');

    optionGroups.forEach(optionGroup => {
      const title = optionGroup.querySelector(
        '[data-option-title]',
      ) as HTMLElement;
      const optionsList = optionGroup.querySelector(
        '[data-product-options-list]',
      );

      if (!title || !optionsList) {
        return;
      }

      const value = optionsList.querySelector(
        'input:checked',
      ) as HTMLInputElement;

      if (!value) {
        return;
      }

      setProductOptions([
        ...productOptions,
        `${title.innerText} ${value.value}`,
      ]);
    });
  };

  const getProductPrice = () => {
    const priceElement = document.querySelector(
      '[data-product-price]',
    ) as HTMLElement;
    const price = priceElement.innerText;
    const fixedPrice = priceElement.getAttribute('data-fp');

    setVariantPrice(price);

    if (fixedPrice) {
      setVariantFixedPrice(fixedPrice);
    }
  };

  const classes = clsx(
    theme.root,
    {
      [theme.open as any]: open,
    },
    className,
  );

  return (
    <ComponentProp className={classes} onClick={handleClick} {...rest}>
      <div
        className={theme.container}
        onMouseEnter={() => setDrawerHovered(true)}
        onMouseLeave={() => setDrawerHovered(false)}
      >
        <span
          className={theme.close}
          onClick={closeDrawer}
          onKeyDown={handleCloseKeyDown}
          role="button"
          tabIndex={0}
        >
          <span className={theme.closeLabel}>{closeText}</span>
        </span>
        <div className={theme.message}>{successMessage}</div>
        <div className={theme.productCard}>
          <img className={theme.productImage} src={productImage} />
          <div className={theme.productDetails}>
            <span className={theme.productTitle}>{productTitle}</span>

            {productComplexColor && (
              <span className={theme.productComplexColor}>
                {productComplexColor}
              </span>
            )}

            {productOptions.map((option, index) => {
              return (
                <span key={index} className={theme.productOption}>
                  {option}
                </span>
              );
            })}

            <span className={theme.productPrice} data-fp={variantFixedPrice}>
              {variantPrice}
            </span>
          </div>
        </div>
        {children}

        <div className={theme.actionsWrapper}>
          {bagLinkText && bagUrl && (
            <a href={bagUrl} className={theme.bagButton}>
              {bagLinkText}&nbsp;
              {currentQuantity && <span>({currentQuantity})</span>}
            </a>
          )}

          {checkoutLinkText && localisedCheckoutUrl && (
            <a href={localisedCheckoutUrl} id={checkoutIdClass} className={theme.checkoutButton}>
              {checkoutLinkText}
            </a>
          )}
        </div>
      </div>
    </ComponentProp>
  );
};
